import React, { useState } from 'react';

const CommonTable = ({
  columns,
  data = [],
  loading = false,
  loadingMessage = "Loading...",
  noDataMessage = "No data found",
  tableClassName = "table table-bordered dt-responsive table-striped nowrap w-100",
  id = "",
  hideHeader = false,
  disablePagination = false,
  containerStyle = {},
  className = "",
  style = {}
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const totalPages = Math.ceil((data?.length || 0) / itemsPerPage);
  const validPage = (currentPage > totalPages && totalPages > 0) ? 1 : currentPage;
  const startIndex = (validPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = disablePagination ? (data || []) : (data ? data.slice(startIndex, endIndex) : []);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pages = [];

    const addPage = (pageNumber) => {
      pages.push(
        <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
          <a
            className="page-link"
            href="#!"
            onClick={(e) => { e.preventDefault(); handlePageChange(pageNumber); }}
          >
            {pageNumber}
          </a>
        </li>
      );
    };

    const addEllipsis = (key) => {
      pages.push(
        <li key={key} className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    };

    let pageNumbers = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let middle1, middle2;

      if (currentPage <= 2) {
        middle1 = 2;
        middle2 = 3;
      } else if (currentPage >= totalPages - 1) {
        middle1 = totalPages - 2;
        middle2 = totalPages - 1;
      } else {
        middle1 = currentPage;
        middle2 = currentPage + 1;
      }

      pageNumbers = [1, middle1, middle2, totalPages];
    }

    let lastNumber = 0;
    pageNumbers.forEach((num) => {
      if (num - lastNumber > 1) {
        addEllipsis(`ellipsis-${lastNumber}`);
      }
      addPage(num);
      lastNumber = num;
    });

    return pages;
  };

  return (
    <div className={`common-table-wrapper ${className}`} style={style}>
      {/* Desktop/Tablet View (Show always if hideHeader is true) */}
      <div
        className={`table-responsive ${hideHeader ? '' : 'd-none d-md-block'}`}
        style={containerStyle}
      >
        <table className={`${tableClassName} align-middle`} id={id}>
          {!hideHeader && (
            <thead>
              <tr>
                {!disablePagination && <th>No.</th>}
                {columns.map((col, index) => (
                  <th key={index} className={col.headerClassName || ''} style={col.headerStyle || {}}>
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {!disablePagination && <td>{rowIndex + startIndex + 1}</td>}
                  {columns.map((col, colIndex) => (
                    <td key={colIndex} className={col.className || ''} style={col.style || {}}>
                      {col.render ? col.render(row, rowIndex + startIndex, {
                        rowIndex,
                        totalOnPage: currentData.length,
                        isLastTwo: currentData.length > 1 ? (rowIndex >= currentData.length - 2) : true,
                        startIndex,
                        totalItems: data?.length || 0
                      }) : row[col.accessor]}
                    </td>
                  ))}
                </tr>
              ))
            ) : loading ? (
              <tr>
                <td colSpan={columns.length + (disablePagination ? 0 : 1)} className="text-center py-4">
                  {loadingMessage}
                </td>
              </tr>
            ) : (
              <tr>
                <td colSpan={columns.length + (disablePagination ? 0 : 1)} className="text-center py-4">
                  {noDataMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View (Only if hideHeader is false) */}
      {!hideHeader && (
        <div className="d-block d-md-none" style={containerStyle}>
          {currentData.length > 0 ? (
            currentData.map((row, rowIndex) => {
              // Find specific columns to treat differently (Date for header, Action for footer)
              const dateColIndex = columns.findIndex(c => c.header?.toLowerCase().includes('date') || c.accessor?.toLowerCase().includes('date'));
              const dateCol = dateColIndex !== -1 ? columns[dateColIndex] : null;

              const actionColIndex = columns.findIndex(
                c => c.header?.toLowerCase() === 'action' ||
                  c.header?.toLowerCase() === 'actions' ||
                  c.accessor?.toLowerCase() === 'action' ||
                  c.accessor?.toLowerCase() === 'actions'
              );
              const actionCol = actionColIndex !== -1 ? columns[actionColIndex] : null;

              return (
                <div key={rowIndex} className="card mb-3 shadow-none" style={{ borderRadius: '12px', border: '1px solid #edf2f7', overflow: 'visible' }}>
                  {/* Card Header */}
                  <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ borderBottom: '1px solid #edf2f7' }}>
                    <span className="fw-bold font-size-14 badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1" style={{ borderRadius: '6px' }}>
                      #{disablePagination ? rowIndex + 1 : rowIndex + startIndex + 1}
                    </span>
                    {dateCol && (
                      <span className="text-muted font-size-13 d-flex align-items-center">
                        <i className="mdi mdi-calendar-month-outline me-1 fs-5 text-primary"></i>
                        {dateCol.render ? dateCol.render(row, rowIndex + startIndex, {
                          rowIndex,
                          totalOnPage: currentData.length,
                          isLastTwo: currentData.length > 1 ? (rowIndex >= currentData.length - 2) : true,
                          startIndex,
                          totalItems: data?.length || 0
                        }) : row[dateCol.accessor]}
                      </span>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="card-body p-0">
                    {columns.map((col, colIndex) => {
                      // Skip Date and Action columns since they are placed in header/footer
                      if (colIndex === dateColIndex || colIndex === actionColIndex) return null;

                      return (
                        <div key={colIndex} className="d-flex justify-content-between align-items-center px-3 py-2" style={{ borderBottom: '1px solid #f1f3f7' }}>
                          <span className="text-muted font-size-13" style={{ minWidth: '35%' }}>
                            {col.header || '-'}
                          </span>
                          <div className="font-size-14 fw-medium text-dark text-end text-break">
                            {col.render ? col.render(row, rowIndex + startIndex, {
                              rowIndex,
                              totalOnPage: currentData.length,
                              isLastTwo: currentData.length > 1 ? (rowIndex >= currentData.length - 2) : true,
                              startIndex,
                              totalItems: data?.length || 0
                            }) : row[col.accessor]}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Card Footer (Action) */}
                  {actionCol && (() => {
                    const actionContent = actionCol.render ? actionCol.render(row, rowIndex + startIndex, {
                      rowIndex,
                      totalOnPage: currentData.length,
                      isLastTwo: currentData.length > 1 ? (rowIndex >= currentData.length - 2) : true,
                      startIndex,
                      totalItems: data?.length || 0
                    }) : row[actionCol.accessor];
                    if (!actionContent) return null;
                    return (
                      <div className="p-2 bg-white" style={{ overflow: 'visible' }}>
                        <div className="mobile-action-container d-flex justify-content-center align-items-center w-100">
                          {actionContent}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })
          ) : loading ? (
            <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body text-center py-4">{loadingMessage}</div>
            </div>
          ) : (
            <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: '12px' }}>
              <div className="card-body text-center py-4">{noDataMessage}</div>
            </div>
          )}
        </div>
      )}

      {!disablePagination && data && data.length > 0 && (
        <div className="pagination-wrapper mt-2">
          <div className="row align-items-center g-2">
            <div className="col-sm-12 col-md-6 d-flex flex-column flex-sm-row align-items-center justify-content-center justify-content-md-start">
              <div className="dataTables_length me-sm-3 mb-2 mb-sm-0">
                <label className="d-flex align-items-center mb-0 font-size-13 text-muted">
                  Show
                  <select
                    className="form-select form-select-sm d-inline-block w-auto mx-2 bg-white"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {[10, 20, 30, 40, 50].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  entries
                </label>
              </div>
              <div className="dataTables_info text-muted font-size-12 d-none d-sm-block">
                Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length} entries
              </div>
            </div>

            <div className="col-sm-12 col-md-6 d-flex justify-content-center justify-content-md-end mt-2 mt-md-0">
              <ul className="pagination pagination-sm mb-0 flex-wrap justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <a
                    className="page-link"
                    href="#!"
                    onClick={(e) => { e.preventDefault(); if (currentPage > 1) handlePageChange(currentPage - 1); }}
                  >
                    <i className="mdi mdi-chevron-left" />
                  </a>
                </li>
                {renderPageNumbers()}
                <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                  <a
                    className="page-link"
                    href="#!"
                    onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) handlePageChange(currentPage + 1); }}
                  >
                    <i className="mdi mdi-chevron-right" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonTable;
